import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { cn } from "@/lib/utils";

const alertVariants = cva(
	"relative w-full rounded-(--radius-sm) border px-4 py-3 text-sm [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:top-4 [&>svg]:left-4 [&>svg]:text-(--color-foreground) [&>svg~*]:pl-7",
	{
		variants: {
			variant: {
				default: "bg-(--color-card) text-(--color-foreground)",
				destructive:
					"border-(--color-error-200) bg-(--color-error-50) text-(--color-error-700) [&>svg]:text-(--color-error-500)",
				success:
					"border-(--color-success-200) bg-(--color-success-50) text-(--color-success-700) [&>svg]:text-(--color-success-500)",
				warning:
					"border-(--color-warning-200) bg-(--color-warning-50) text-(--color-warning-700) [&>svg]:text-(--color-warning-500)",
				info: "border-(--color-info-200) bg-(--color-info-50) text-(--color-info-700) [&>svg]:text-(--color-info-500)",
			},
		},
		defaultVariants: {
			variant: "default",
		},
	},
);

const Alert = React.forwardRef<
	HTMLDivElement,
	React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, ...props }, ref) => (
	<div
		ref={ref}
		role="alert"
		className={cn(alertVariants({ variant }), className)}
		{...props}
	/>
));
Alert.displayName = "Alert";

const AlertTitle = React.forwardRef<
	HTMLParagraphElement,
	React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
	<h5
		ref={ref}
		className={cn(
			"mb-1 font-medium normal-case leading-none tracking-tight",
			className,
		)}
		{...props}
	/>
));
AlertTitle.displayName = "AlertTitle";

const AlertDescription = React.forwardRef<
	HTMLParagraphElement,
	React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
	<div
		ref={ref}
		className={cn("text-sm [&_p]:leading-relaxed", className)}
		{...props}
	/>
));
AlertDescription.displayName = "AlertDescription";

export { Alert, AlertTitle, AlertDescription };
